/**
 * Signer PIN setup — /signer/pin-setup?t=<token>.
 *
 * The operator texts this single-use link (~30 minutes) while sitting with the
 * physician at onboarding; the live coordination IS the identity verification.
 * The physician then chooses a 6-digit PIN on their own device. Only the digest
 * leaves this page, and the operator's UI shows nothing but "PIN set ✓".
 *
 * That operator ignorance is the point, not an implementation detail: the same
 * person prepares the forms and controls link dispatch, so a signature only
 * carries evidentiary weight if they demonstrably could not have entered the
 * PIN themselves. There is no self-service recovery — a forgotten PIN goes back
 * through the operator, who verifies identity personally and re-issues a setup
 * link to the same verified mobile. See docs/pilot/MODEL_A_PILOT_PLAN.md §3.3.
 *
 * The token in the URL is the whole credential, so no session and no auth
 * header are involved on either call.
 */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import ParseoLogo from "../../shared/ui/ParseoLogo";
import { pinSetupRedeem, pinSetupSubmit } from "../../lib/bridgeClient";
import { PIN_LENGTH, pinDigest, validatePin } from "../../lib/signerPin";

const STATE = {
  CHECKING: "checking",
  INVALID: "invalid",
  FORM: "form",
  SAVING: "saving",
  DONE: "done",
};

export default function PinSetupPage() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";

  const [view, setView] = useState(token ? STATE.CHECKING : STATE.INVALID);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    pinSetupRedeem(token)
      .then(() => {
        if (!cancelled) setView(STATE.FORM);
      })
      .catch(() => {
        if (!cancelled) setView(STATE.INVALID);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(ev?: React.FormEvent<HTMLFormElement>) {
    ev?.preventDefault?.();
    if (view === STATE.SAVING) return;

    // Client mirror of the server's rules — a courtesy so the physician isn't
    // bounced by a round-trip. The server re-checks every one of them.
    const localMsg = validatePin(pin);
    if (localMsg) {
      setError(localMsg);
      return;
    }
    if (pin !== confirmPin) {
      setError("Those two PINs don't match.");
      return;
    }

    setError(null);
    setView(STATE.SAVING);
    try {
      const digest = await pinDigest(pin);
      await pinSetupSubmit(token, digest);
      // Don't leave the chosen PIN sitting in component state afterwards.
      setPin("");
      setConfirmPin("");
      setView(STATE.DONE);
    } catch (err) {
      const detail = (err as { detail?: string }).detail;
      const status = (err as { status?: number }).status;
      if (status === 401) {
        setView(STATE.INVALID);
        return;
      }
      // The server is authoritative about which PINs it accepts, so show its
      // reason verbatim rather than paraphrasing it into drift.
      setError(detail || "We couldn't save that PIN just now. Please try again.");
      setView(STATE.FORM);
    }
  }

  if (view === STATE.CHECKING) {
    return frame(
      <>
        <ParseoLogo animated size={64} />
        <p style={{ color: "#5b6e7c", marginTop: 14 }}>Checking your link…</p>
      </>
    );
  }

  if (view === STATE.INVALID) {
    return frame(
      <>
        <h2 style={{ color: "#c25450", margin: "16px 0 8px" }}>Link not available</h2>
        <p style={{ color: "#5b6e7c", maxWidth: 420, lineHeight: 1.5, margin: 0 }}>
          This setup link has already been used or has expired — they're good for
          about 30 minutes. Contact Parseo Health and we'll send you a fresh one.
        </p>
      </>
    );
  }

  if (view === STATE.DONE) {
    return frame(
      <>
        <ParseoLogo size={56} />
        <h2 style={{ color: "#182a33", margin: "16px 0 8px" }}>Your PIN is set.</h2>
        <p style={{ color: "#5b6e7c", maxWidth: 420, lineHeight: 1.55, margin: 0 }}>
          You'll enter it whenever you open a signing link. Parseo never sees it
          — only a one-way hash is stored — so keep it somewhere you'll remember.
        </p>
      </>
    );
  }

  const saving = view === STATE.SAVING;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          maxWidth: 440,
          width: "100%",
          backgroundColor: "white",
          padding: 32,
          borderRadius: 12,
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <ParseoLogo size={40} />
        <h2 style={{ color: "#182a33", margin: "14px 0 8px" }}>Set your signing PIN</h2>
        <p style={{ color: "#5b6e7c", marginBottom: 22, lineHeight: 1.5 }}>
          Signing links open a patient's completed form, so they ask for a{" "}
          {PIN_LENGTH}-digit PIN as well as the link itself. Choose one now —
          only you will know it, and you'll use the same one every time.
        </p>

        <label
          htmlFor="new-pin"
          style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#182a33", marginBottom: 6 }}
        >
          Choose a {PIN_LENGTH}-digit PIN
        </label>
        <input
          id="new-pin"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          aria-label={`Choose a ${PIN_LENGTH}-digit PIN`}
          maxLength={PIN_LENGTH}
          autoFocus
          disabled={saving}
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH));
            if (error) setError(null);
          }}
          style={pinInputStyle(saving)}
        />

        <label
          htmlFor="confirm-pin"
          style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#182a33", margin: "14px 0 6px" }}
        >
          Enter it again
        </label>
        <input
          id="confirm-pin"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          aria-label="Enter it again"
          maxLength={PIN_LENGTH}
          disabled={saving}
          value={confirmPin}
          onChange={(e) => {
            setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, PIN_LENGTH));
            if (error) setError(null);
          }}
          style={pinInputStyle(saving)}
        />

        {error && (
          <p style={{ color: "#c25450", marginTop: 12, marginBottom: 0, fontSize: 14, lineHeight: 1.45 }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          style={{
            width: "100%",
            marginTop: 20,
            padding: "12px 14px",
            fontSize: 16,
            fontWeight: 600,
            border: "none",
            borderRadius: 8,
            backgroundColor: saving ? "#bbb" : "#1b5e6e",
            color: "white",
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving…" : "Set my PIN"}
        </button>

        <p style={{ fontSize: 12.5, color: "#5b6e7c", lineHeight: 1.5, margin: "16px 0 0" }}>
          Avoid repeated digits (111111) and sequences (123456) — they'll be
          refused.
        </p>
      </form>
    </div>
  );
}

function pinInputStyle(disabled: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: "12px 14px",
    fontSize: 22,
    letterSpacing: "0.35em",
    textAlign: "center",
    border: "1px solid #e6dccb",
    borderRadius: 8,
    backgroundColor: disabled ? "#f3f3f3" : "white",
    color: "#182a33",
    boxSizing: "border-box",
  };
}

function frame(content: React.ReactNode) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
        textAlign: "center",
      }}
    >
      {content}
    </div>
  );
}
